import { Item } from 'shared-types';
import {
  ROOM_EXTRA_PREFIX,
  SPA_GUEST_EXTRA_PREFIX,
  SPA_SELECTION_EXTRA_PREFIX,
} from '../../../common/components/BasketDrawer/v2/adapters/toBasketCardItem';

type ExtraWithId = { id?: number | string; extra?: { id?: number | string } };

const extraId = (extra: ExtraWithId): string => String(extra?.id ?? extra?.extra?.id ?? '');

const withoutRoomExtra = (item: Item, extraId: string): Item => {
  const roomExtras = item.bookable?.roomExtras;
  if (!Array.isArray(roomExtras)) return item;

  const remaining = roomExtras.filter((roomExtra) => String(roomExtra.id) !== extraId);
  if (remaining.length === roomExtras.length) return item;

  return { ...item, bookable: { ...item.bookable, roomExtras: remaining } } as Item;
};

const withoutSelectionExtra = (item: Item, targetExtraId: string): Item => {
  const selectionGroups = item.selectionGroups;
  if (!Array.isArray(selectionGroups)) return item;

  let changed = false;
  const remaining = selectionGroups.map((group) => {
    if (!group?.isExtras) return group;

    const selections = (group as { selections?: unknown }).selections;
    if (!Array.isArray(selections)) return group;

    const kept = selections.filter(
      (selection) => String((selection as ExtraWithId)?.extra?.id ?? '') !== targetExtraId
    );
    if (kept.length === selections.length) return group;

    changed = true;
    return { ...group, selections: kept };
  });

  if (!changed) return item;

  return { ...item, selectionGroups: remaining } as Item;
};

const withoutGuestExtra = (item: Item, targetId: string): Item => {
  const guestDetails = item.guestDetails;
  if (!Array.isArray(guestDetails)) return item;

  let changed = false;
  const remaining = guestDetails.map((guest) => {
    const extras = guest?.extras;
    if (!Array.isArray(extras)) return guest;

    const kept = extras.filter((extra) => extraId(extra as ExtraWithId) !== targetId);
    if (kept.length === extras.length) return guest;

    changed = true;
    return { ...guest, extras: kept };
  });

  if (!changed) return item;

  return { ...item, guestDetails: remaining } as Item;
};

export const removeExtraFromBasketItem = (
  items: Item[],
  itemId: string | number,
  extraKey: string
): Item[] => {
  const index = items.findIndex((item) => String(item.id) === String(itemId));
  if (index === -1) return items;

  const item = items[index];
  let updated = item;

  if (extraKey.startsWith(ROOM_EXTRA_PREFIX)) {
    updated = withoutRoomExtra(item, extraKey.slice(ROOM_EXTRA_PREFIX.length));
  } else if (extraKey.startsWith(SPA_GUEST_EXTRA_PREFIX)) {
    updated = withoutGuestExtra(item, extraKey.slice(SPA_GUEST_EXTRA_PREFIX.length));
  } else if (extraKey.startsWith(SPA_SELECTION_EXTRA_PREFIX)) {
    updated = withoutSelectionExtra(item, extraKey.slice(SPA_SELECTION_EXTRA_PREFIX.length));
  }

  if (updated === item) return items;

  const next = [...items];
  next[index] = updated;
  return next;
};
